import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { authApi } from '../lib/api'
import AuthCardLayout, { errorBoxStyle, fieldInputStyle, fieldLabelStyle, primaryButtonStyle } from '../components/AuthCardLayout'
import ProfileDetailsFields, { emptyProfileDetails, profileDetailsPayload, validateProfileDetails, type ProfileDetails } from '../components/ProfileDetailsFields'

// The step after signing up with Google or Facebook: the same details email sign-up asks for,
// minus the password. ProfileGate sends anyone without an address here until it is done.
export default function CompleteProfile() {
  const { user, refreshUser, logout } = useAuth()
  const navigate = useNavigate()

  const [firstName, setFirstName] = useState(user?.firstName ?? '')
  const [lastName, setLastName] = useState(user?.lastName ?? '')
  const [details, setDetails] = useState<ProfileDetails>(emptyProfileDetails)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!firstName.trim()) {
      setError('Please enter your first name')
      return
    }
    const problem = validateProfileDetails(details)
    if (problem) {
      setError(problem)
      return
    }

    setSaving(true)
    try {
      await authApi.completeProfile({ firstName: firstName.trim(), lastName: lastName.trim(), ...profileDetailsPayload(details) })
      await refreshUser()
      navigate('/dashboard', { replace: true })
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not save your details. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <AuthCardLayout title={`Welcome${user?.firstName ? `, ${user.firstName}` : ''}!`} subtitle="One last step: add your details to finish setting up your igotthatus.com account.">
      {error && <div style={errorBoxStyle}>{error}</div>}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label style={fieldLabelStyle}>Email</label>
          <input type="email" value={user?.email ?? ''} disabled style={{ ...fieldInputStyle, background: '#F7FAF7', color: 'var(--slate)' }} />
        </div>
        <div>
          <label htmlFor="firstName" style={fieldLabelStyle}>First name</label>
          <input id="firstName" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} style={fieldInputStyle} autoComplete="given-name" required />
        </div>
        <div>
          <label htmlFor="lastName" style={fieldLabelStyle}>Last name</label>
          <input id="lastName" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} style={fieldInputStyle} autoComplete="family-name" />
        </div>

        <ProfileDetailsFields values={details} onChange={(patch) => setDetails((d) => ({ ...d, ...patch }))} />

        <button type="submit" disabled={saving} style={primaryButtonStyle(saving)}>
          {saving ? 'Saving...' : 'Finish setting up'}
        </button>
      </form>

      <p style={{ textAlign: 'center', marginTop: '1.25rem', marginBottom: 0, fontSize: '0.875rem', color: 'var(--slate-light)' }}>
        Not you?{' '}
        <button type="button" onClick={() => { logout(); navigate('/login', { replace: true }) }} style={{ background: 'none', border: 'none', color: 'var(--forest-green)', fontWeight: 600, cursor: 'pointer', minHeight: '44px', padding: '0 0.25rem' }}>
          Sign out
        </button>
      </p>
    </AuthCardLayout>
  )
}
